<template>
  <teleport to="body">
    <div
      v-if="open"
      class="modal fade show"
      style="display: block;"
      id="enviarSeguimientoModal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="enviarSeguimientoModalLabel"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="enviarSeguimientoModalLabel">
              Enviar {{ nombreDocumento }} por WhatsApp
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="handleClose"></button>
          </div>
          <div class="modal-body">
            <!--
              La frase del correo vale para la FACTURA y no para el RECIBO
              (H-AD). El correo no lo manda este sistema: el documento viaja
              con `receptor.email` y lo envía facturación electrónica. El
              contrato de `/v1/recibos` no tiene campo de correo, así que para
              el recibo el enlace es la única entrega — decir lo contrario le
              haría creer al operador que el pagador ya lo recibió.
            -->
            <p class="text-muted small">
              Mandale al cliente el link para consultar su {{ nombreDocumento }}.
              <!-- La promesa del correo vale SÓLO para la factura (H-AD): es
                   facturación electrónica la que la envía. Para la nota de
                   crédito el payload lleva el correo, pero que FE la mande no
                   está verificado, y prometerlo le haría creer al operador que
                   el pagador ya la recibió (SPEC_NOTAS_DE_CREDITO §4). -->
              <template v-if="tipoDocumento === 'FACTURA'">
                Si el cliente tiene correo cargado, además la recibe por email
                automáticamente.
              </template>
              <template v-else>
                Este documento se entrega sólo por este enlace.
              </template>
            </p>
            <p v-if="mensaje" class="small border rounded p-2 bg-light" data-test="whatsapp-mensaje">
              {{ mensaje }}
            </p>

            <div class="mb-3">
              <label class="form-label small">WhatsApp</label>
              <div class="input-group">
                <select class="form-select flex-grow-0 w-auto" v-model="paisCodigo" aria-label="País">
                  <option v-for="p in PAISES" :key="p.codigo" :value="p.codigo">
                    {{ p.bandera }} +{{ p.codigo }}
                  </option>
                </select>
                <input
                  type="tel"
                  class="form-control"
                  v-model="telefono"
                  :placeholder="paisActual.placeholder"
                />
                <button class="btn btn-outline-success" :disabled="!telefonoValido" @click="enviarPorWhatsapp">
                  <i class="bi bi-whatsapp me-1"></i>Enviar
                </button>
              </div>
              <div v-if="telefono && telefonoValido" class="form-text small">
                Se enviará a +{{ telefonoNormalizado }}
              </div>
              <div v-else-if="telefono && motivo" class="form-text small text-danger">
                {{ motivo }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline-secondary" @click="handleClose">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="open" class="modal-backdrop fade show" @click="handleClose"></div>
  </teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import {
  PAISES,
  PAIS_POR_DEFECTO,
  buscarPais,
  separarPais,
  resolverNumero,
  linkWhatsapp
} from '@/utils/whatsapp.mjs'

/* global defineProps, defineEmits */
const props = defineProps({
  open: { type: Boolean, default: false },
  trackingUrl: { type: String, default: '' },
  telefonoInicial: { type: String, default: '' },
  // 'FACTURA' | 'RECIBO' | 'NOTA_CREDITO' | 'RECIBO_ANULACION'. Cambia lo que
  // el modal puede prometer: el correo automático existe sólo para la factura
  // (H-AD).
  tipoDocumento: { type: String, default: 'FACTURA' },
  // El texto ya armado por `mensajeDeEntrega` (RN-151). Si no viene, se usa el
  // de siempre — los llamadores de caja no se enteran.
  mensaje: { type: String, default: '' }
})

const esRecibo = computed(() => props.tipoDocumento === 'RECIBO')
const NOMBRES = {
  FACTURA: 'factura', RECIBO: 'recibo', NOTA_CREDITO: 'nota de crédito',
  RECIBO_ANULACION: 'constancia de anulación'
}
const nombreDocumento = computed(() => NOMBRES[props.tipoDocumento] || 'comprobante')

const emit = defineEmits(['cerrado'])

const telefono = ref('')
const paisCodigo = ref(PAIS_POR_DEFECTO)
const paisActual = computed(() => buscarPais(paisCodigo.value))

// La normalizacion vive en src/utils/whatsapp.mjs: los avisos de retiro y de
// envio van a usar la misma y tiene test propio (test/whatsapp.test.mjs).
const numeroResuelto = computed(() => resolverNumero(telefono.value, paisCodigo.value))
const telefonoNormalizado = computed(() => numeroResuelto.value.internacional)
const telefonoValido = computed(() => numeroResuelto.value.valido)
const motivo = computed(() => numeroResuelto.value.motivo)

watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  telefono.value = props.telefonoInicial || ''
  // Default Paraguay salvo que el número guardado venga como `+<pais>...`.
  const { pais } = separarPais(props.telefonoInicial)
  paisCodigo.value = pais?.codigo || PAIS_POR_DEFECTO
})

function handleClose() {
  emit('cerrado')
}

function enviarPorWhatsapp() {
  if (!telefonoValido.value) return
  // Sin API real de WhatsApp en el stack (SPEC_JOBS_PROGRAMADOS_v0.1.md) —
  // mismo patrón que ecommerce-graciela/src/services/whatsappCheckoutService.js:
  // link wa.me con texto prellenado, abierto en pestaña nueva.
  const mensaje = props.mensaje || (esRecibo.value
    ? `Hola, acá podés ver y descargar tu recibo: ${props.trackingUrl}`
    : `Hola, podés consultar el estado de tu factura acá: ${props.trackingUrl}`)
  window.open(linkWhatsapp(telefonoNormalizado.value, mensaje), '_blank')
}

</script>
